/**
 * Fallback per-endpoint pricing for FAL endpoints used by the 360 video
 * pipeline.
 *
 * Background: many FAL endpoints (kling-video, veo3.1, seedance, …)
 * don't return `usage.cost` in the response the way the OpenRouter-
 * routed LLM endpoints do. The video pipeline still records every call
 * into `fal_usage`, but `direct_cost` ends up as `0.0` for these
 * endpoints — which used to make the History "Cost" column read `—`
 * even on successful runs.
 *
 * The backend now prices every attempt itself (`video/video_pricing.py`)
 * and persists the total per endpoint as `fal_usage.priced_cost`, which
 * is the same figure that job's `generation_log.csv` records. That is
 * the source of truth, and `jobEndpointCosts` prefers it. This module
 * remains the fallback for jobs that ran before the backend persisted
 * it, and its rates are kept consistent with the backend's rather than
 * being a second opinion — where the two once disagreed, the more
 * accurate one won in both places (see `seedancePerCallCost`, whose
 * padded-dimension formula the backend now shares).
 *
 * Every rate here is documented on the model's own FAL page (see
 * `SOURCES` below); we never invent one. An endpoint with no published
 * figure is left out and shows in the History tooltip as "(estimate
 * unavailable)" rather than padding the bill with a made-up number.
 *
 * SOURCES (last verified 2026-05-06):
 *   - https://fal.ai/models/fal-ai/kling-video/v3/pro/image-to-video
 *   - https://fal.ai/models/fal-ai/kling-video/v3/4k/image-to-video
 *   - https://fal.ai/models/bytedance/seedance-2.0/image-to-video
 *   - https://fal.ai/models/fal-ai/veo3.1/fast/first-last-frame-to-video
 *   - https://fal.ai/models/fal-ai/bria/background/remove
 */
import type { FalEndpointUsage, Job, VideoGenConfig } from '../types';

/** Pricing source: did the number come from FAL or our static table? */
export type CostSource = 'billed' | 'estimated' | 'unknown';

export interface EndpointCost {
  endpoint: string;
  cost: number;
  source: CostSource;
  /** True when this endpoint is missing from our fallback table. */
  unknown?: boolean;
}

// ---------- shared parsers ------------------------------------------------

/**
 * Parse a duration value out of `VideoGenConfig.duration`. The field is
 * a model-specific string — Veo uses `"4s" | "6s" | "8s"`, Kling uses
 * `"3" | "4" | … | "15"`, Seedance allows `"auto"` plus the same
 * numeric range. Returns `null` for unparseable values; callers treat
 * that as "estimate unavailable".
 *
 * `seedanceAutoDefault` lets the Seedance estimator substitute a
 * sensible median when the user picked `"auto"` (the model itself
 * picks based on the prompt at runtime, so we can't know exactly).
 */
function parseDurationSeconds(
  value: string | null | undefined,
  seedanceAutoDefault?: number,
): number | null {
  if (value == null) return null;
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return null;
  if (trimmed === 'auto') {
    return seedanceAutoDefault ?? null;
  }
  // "4s" → 4, "8s" → 8, "3" → 3
  const stripped = trimmed.endsWith('s') ? trimmed.slice(0, -1) : trimmed;
  const num = Number.parseFloat(stripped);
  return Number.isFinite(num) && num > 0 ? num : null;
}

type ResolutionFamily = '480p' | '720p' | '1080p' | '4k';

/** Map an arbitrary resolution string to a known family, or `null`. */
function classifyResolution(value: string | null | undefined): ResolutionFamily | null {
  if (!value) return null;
  const v = value.trim().toLowerCase();
  if (v === '480p') return '480p';
  if (v === '720p') return '720p';
  if (v === '1080p') return '1080p';
  if (v === '4k' || v === '2160p') return '4k';
  return null;
}

// ---------- Veo 3.1 Fast ---------------------------------------------------

/**
 * Veo 3.1 Fast is per-second with audio + resolution-tier modifiers:
 *   - 720p / 1080p: $0.10/s audio off, $0.15/s audio on
 *   - 4k:           $0.30/s audio off, $0.35/s audio on
 *
 * Source: https://fal.ai/models/fal-ai/veo3.1/fast/first-last-frame-to-video
 */
function veo31FastPerCallCost(config: VideoGenConfig): number | null {
  const seconds = parseDurationSeconds(config.duration);
  if (seconds == null) return null;
  const family = classifyResolution(config.resolution) ?? '720p';
  const audio = config.generate_audio;
  let perSec: number;
  if (family === '4k') {
    perSec = audio ? 0.35 : 0.3;
  } else {
    perSec = audio ? 0.15 : 0.1;
  }
  return perSec * seconds;
}

// ---------- Seedance 2.0 ---------------------------------------------------

/**
 * $0.014 / 1000 tokens up to 1080p and $0.008 / 1000 at 4k, where
 * tokens = (w × h × seconds × 24) / 1024.
 */
const SEEDANCE_TOKEN_RATE_PER_1000: Record<ResolutionFamily, number> = {
  '480p': 0.014,
  '720p': 0.014,
  '1080p': 0.014,
  '4k': 0.008,
};
/**
 * Seedance's `duration: "auto"` lets the model pick based on the prompt
 * (4–15 s). We don't know the exact pick at submission time, so the
 * estimator substitutes a 5 s median. Tooltip shows `(estimated)` so
 * the imprecision is obvious.
 */
const SEEDANCE_AUTO_DURATION_SECONDS = 5;

/**
 * Compute a Seedance video's billable pixel dimensions from
 * `(resolution, aspect_ratio)`.
 *
 * Convention: the resolution name (`720p`, `1080p`, …) refers to the
 * *shorter* video dimension. We derive the other side from the aspect
 * ratio so the formula matches what FAL bills:
 *   - 720p × 16:9 → 1280×720
 *   - 720p × 9:16 → 720×1280
 *   - 720p × 21:9 → 1680×720
 *   - 720p × 1:1  → 720×720
 *
 * Both dimensions are then padded UP to the next multiple of 16. H.264
 * (which Seedance encodes into) operates on 16×16 macroblocks, so
 * "1080p" videos are actually encoded at 1920×1088, not 1920×1080 —
 * and FAL bills against the encoded pixel count. The 720p case is
 * unaffected (1280 and 720 are both already divisible by 16, which is
 * why FAL's quoted `$0.3024/s` for 720p still matches our formula
 * exactly).
 *
 * Empirical anchor: a 4-second 1080p × 16:9 Seedance call billed at
 * $2.7475 (per-call), corresponding to ~196k tokens. Padded
 * 1920×1088 yields ~196k tokens vs. the unpadded 1920×1080's ~194k —
 * the padded number lands within ~0.2% of FAL's actual bill. Without
 * padding the estimator was ~1% low on 1080p runs.
 *
 * "auto" aspect ratio defaults to 16:9 (the most common landscape
 * default; user's pipeline currently runs on 16:9 anyway).
 */
function seedanceDimensions(
  resolution: string | null | undefined,
  aspectRatio: string | null | undefined,
): { w: number; h: number } | null {
  const family = classifyResolution(resolution);
  // Seedance only offers 480p / 720p / 1080p, but we accept 4k for
  // safety in case FAL adds it later.
  const heightByFamily: Record<ResolutionFamily, number> = {
    '480p': 480,
    '720p': 720,
    '1080p': 1080,
    '4k': 2160,
  };
  if (!family) return null;
  const baseHeight = heightByFamily[family];

  const ar = (aspectRatio ?? '').trim().toLowerCase();
  const arNorm = !ar || ar === 'auto' ? '16:9' : ar;
  const [aw, ah] = arNorm.split(':').map((s) => Number.parseFloat(s));
  if (!Number.isFinite(aw) || !Number.isFinite(ah) || aw <= 0 || ah <= 0) {
    return null;
  }

  // Resolution name = the shorter side, regardless of orientation.
  let rawW: number;
  let rawH: number;
  if (ah > aw) {
    rawW = baseHeight;
    rawH = (baseHeight * ah) / aw;
  } else {
    rawH = baseHeight;
    rawW = (baseHeight * aw) / ah;
  }

  // Pad to H.264 macroblock alignment (16×16). 1080p's height of 1080
  // becomes 1088; 480p × 16:9's width of 854 becomes 864; the 720p
  // family stays exactly 1280×720 (already aligned).
  const padTo16 = (n: number) => Math.ceil(n / 16) * 16;
  return { w: padTo16(rawW), h: padTo16(rawH) };
}

function seedancePerCallCost(config: VideoGenConfig): number | null {
  const seconds = parseDurationSeconds(
    config.duration,
    SEEDANCE_AUTO_DURATION_SECONDS,
  );
  if (seconds == null) return null;
  const family = classifyResolution(config.resolution);
  if (!family) return null;
  const dims = seedanceDimensions(config.resolution, config.aspect_ratio);
  if (!dims) return null;
  const tokens = (dims.w * dims.h * seconds * 24) / 1024;
  return (tokens / 1000) * SEEDANCE_TOKEN_RATE_PER_1000[family];
}

// ---------- Bria background remove ----------------------------------------

/**
 * $0.018 per generation — a flat rate that doesn't depend on the
 * request, so no config is needed to price it.
 *
 * Source: https://fal.ai/models/fal-ai/bria/background/remove
 */
const BRIA_BACKGROUND_REMOVE_PER_CALL = 0.018;

// ---------- Kling Video v3 Pro --------------------------------------------

/**
 * Kling v3 Pro is per-second with audio modifier:
 *   - $0.112/s audio off
 *   - $0.168/s audio on
 *   - $0.196/s audio + voice control (we never enable voice control)
 *
 * Source: https://fal.ai/models/fal-ai/kling-video/v3/pro/image-to-video
 */
const KLING_V3_PRO_PER_SEC_AUDIO_OFF = 0.112;
const KLING_V3_PRO_PER_SEC_AUDIO_ON = 0.168;

function klingV3ProPerCallCost(config: VideoGenConfig): number | null {
  const seconds = parseDurationSeconds(config.duration);
  if (seconds == null) return null;
  const perSec = config.generate_audio
    ? KLING_V3_PRO_PER_SEC_AUDIO_ON
    : KLING_V3_PRO_PER_SEC_AUDIO_OFF;
  return perSec * seconds;
}

// ---------- Kling Video v3 4K ---------------------------------------------

/**
 * Kling v3 4K is a flat $0.42/s — audio toggle does not change the
 * rate (per the FAL model page).
 *
 * Source: https://fal.ai/models/fal-ai/kling-video/v3/4k/image-to-video
 */
const KLING_V3_4K_PER_SEC = 0.42;

function klingV34kPerCallCost(config: VideoGenConfig): number | null {
  const seconds = parseDurationSeconds(config.duration);
  if (seconds == null) return null;
  return KLING_V3_4K_PER_SEC * seconds;
}

// ---------- dispatcher -----------------------------------------------------

/**
 * Compute a fallback per-call cost for a FAL endpoint when FAL itself
 * didn't return a `direct_cost`. Returns `null` for endpoints we don't
 * know the pricing for — callers should treat this as "unknown" rather
 * than "free".
 */
function estimatedPerCallCost(
  endpoint: string,
  config: VideoGenConfig | null,
): number | null {
  // Flat rates first — they need nothing from the request.
  if (endpoint.startsWith('fal-ai/bria/background/remove')) {
    return BRIA_BACKGROUND_REMOVE_PER_CALL;
  }
  if (!config) return null;

  // Kling v3 4K must come BEFORE the v3/pro prefix check below because
  // both endpoints share the `fal-ai/kling-video/v3/` prefix.
  if (endpoint.startsWith('fal-ai/kling-video/v3/4k/image-to-video')) {
    return klingV34kPerCallCost(config);
  }
  if (endpoint.startsWith('fal-ai/kling-video/v3/pro/image-to-video')) {
    return klingV3ProPerCallCost(config);
  }
  if (endpoint.startsWith('fal-ai/veo3.1/fast/first-last-frame-to-video')) {
    return veo31FastPerCallCost(config);
  }
  if (endpoint.startsWith('bytedance/seedance-2.0/image-to-video')) {
    return seedancePerCallCost(config);
  }

  return null;
}

/**
 * Per-endpoint cost rollup for a single job, in preference order:
 *
 *  1. `direct_cost` — what FAL actually billed, so it reads as `billed`.
 *  2. `priced_cost` — what the backend priced the job's calls at while
 *     it ran, i.e. exactly what its `generation_log.csv` recorded. Still
 *     an estimate, so it reads as `estimated`.
 *  3. this module's own table, for jobs that predate (2).
 */
export function jobEndpointCosts(job: Job): EndpointCost[] {
  if (!job.fal_usage) return [];
  const out: EndpointCost[] = [];
  for (const [endpoint, usage] of Object.entries(job.fal_usage)) {
    const u = usage as FalEndpointUsage;
    if (u.direct_cost > 0) {
      out.push({ endpoint, cost: u.direct_cost, source: 'billed' });
      continue;
    }
    if (u.priced_cost != null && u.priced_cost > 0) {
      out.push({ endpoint, cost: u.priced_cost, source: 'estimated' });
      continue;
    }
    const perCall = estimatedPerCallCost(endpoint, job.config_used);
    if (perCall == null) {
      out.push({ endpoint, cost: 0, source: 'unknown', unknown: true });
      continue;
    }
    out.push({
      endpoint,
      cost: perCall * u.calls,
      source: 'estimated',
    });
  }
  return out;
}
